// ** React imports
import { useState, useRef, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  IconButton,
} from "@mui/material";
import MuiAvatar from "@mui/material/Avatar";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";

// ** Icon Imports
import Icon from "components/icon";

// ** Custom Component Imports
import CustomAvatar from "components/avatar";
import CustomTextField from "./mui/text-field";
import ChatBotIcon from "images/icons/ChatbotIcon.png";
import ChatBotIconExpanded from "images/icons/ChatbotIconExpanded.png";

// ** Styled Components
const ChatWrapperStartChat = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  height: "100%",
  display: "flex",
  borderRadius: 1,
  alignItems: "center",
  flexDirection: "column",
  justifyContent: "center",
  gap: 6,
}));

const ChatFormWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2.5),
  boxShadow: theme.shadows[1],
  justifyContent: "space-between",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
}));

const ChatLog = ({theme}) => {
  return (
    <Box
        sx={{
            width: "100%",
        }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row-reverse",
            mb: 4,
          }}
        >
          <div>
            <CustomAvatar
              skin="light"
              sx={{
                width: 32,
                height: 32,
                ml: 3,
                fontSize: (theme) => theme.typography.body1.fontSize,
              }}
            >
              AB
            </CustomAvatar>
          </div>

          <Box
            className="chat-body"
            sx={{ maxWidth: ["calc(100% - 5.75rem)", "75%", "65%"] }}
          >
            <Box sx={{ "&:not(:last-of-type)": { mb: 3 } }}>
              <div>
                <Typography
                  sx={{
                    boxShadow: 1,
                    borderRadius: 1,
                    maxWidth: "100%",
                    width: "fit-content",
                    wordWrap: "break-word",
                    p: (theme) => theme.spacing(2.25, 4),
                    ml: "auto",
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    color: "common.white",
                    backgroundColor: "main.main",
                  }}
                >
                  Hi, I'd like to create a new customer record.
                </Typography>
                <Typography variant="body2" sx={{ color: "text.disabled" }}>
                  John Doe - 04/08/2025
                </Typography>
              </div>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            mb: 4,
          }}
        >
          <div>
            <CustomAvatar
              skin="light"
              sx={{
                width: 32,
                height: 32,
                mr: 3,
                fontSize: (theme) => theme.typography.body1.fontSize,
              }}
              src='images/Logo.svg'
            >
              AB
            </CustomAvatar>
          </div>

          <Box
            className="chat-body"
            sx={{ maxWidth: ["calc(100% - 5.75rem)", "75%", "65%"] }}
          >
            <Box sx={{ "&:not(:last-of-type)": { mb: 3 } }}>
              <div>
                <Typography
                  sx={{
                    boxShadow: 1,
                    borderRadius: 1,
                    maxWidth: "100%",
                    width: "fit-content",
                    wordWrap: "break-word",
                    p: (theme) => theme.spacing(2.25, 4),
                    ml: "auto",
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    color: "text.primary",
                    backgroundColor: "background.paper",
                    border: `1px solid ${theme.palette.divider}`
                  }}
                >
                  Ok! Still need More information
                </Typography>
                <Typography variant="body2" sx={{ color: "text.disabled" }}>
                  Cleri Bot - 04/08/2025
                </Typography>
              </div>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const SendMsgForm = () => {
  return (
    <ChatFormWrapper>
      <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
        <CustomTextField
          fullWidth
          placeholder="Ask anything..."
          sx={{
            "& .Mui-focused": { boxShadow: "none !important" },
            "& .MuiInputBase-input:not(textarea).MuiInputBase-inputSizeSmall": {
              p: (theme) => theme.spacing(1.875, 2.5),
            },
            "& .MuiInputBase-root": { border: "0 !important" },
          }}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton size="small" sx={{ color: "text.primary" }}>
          <Icon icon="tabler:microphone" />
        </IconButton>
        <IconButton size="small" sx={{ color: "text.primary" }}>
          <Icon icon="tabler:send" />
        </IconButton>
      </Box>
    </ChatFormWrapper>
  );
};

const Chatbot = () => {
  const theme = useTheme();
  const mdAbove = useMediaQuery(theme.breakpoints.up("md"));
  const chatArea = useRef(null);

  const [isHovered, setIsHovered] = useState(false);
  const [displayChat, setDisplayChat] = useState(false);
  const [chatVisibility, setChatVisibility] = useState('none');
  const [chatIsEmpty, setChatIsEmpty] = useState(false);
  const [chatMaximize, setChatMaximize] = useState(false);

  useEffect(() => {
    setTimeout( ()=> {
      if(displayChat){
        setChatVisibility('flex')
      }else{
        setChatVisibility('none')
      }
    }, 400)
  
  }, [displayChat])
  

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          bottom: 10,
          right: 5,
          zIndex: 10000,
        }}
      >
        <Card
          sx={{
            position: "absolute",
            right: 0,
            bottom: 80,
            minWidth: chatMaximize ? "80vw" : "25vw",
            minHeight: chatMaximize ? "75vh" : "60vh",
            transition: "opacity 0.3s ease-in-out",
            opacity: displayChat ? 1 : 0,
            display: chatVisibility,
            flexDirection: "column",
          }}
        >
          <CardHeader
            title={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ cursor: "default" }}>
                  <Typography variant="h5" color="white">
                    Talk to Cleri
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                    }}
                  >
                    <Typography variant="h5" color="white">
                      •
                    </Typography>
                    <Typography variant="body2" color="white">
                      Online
                    </Typography>
                  </Box>
                </Box>
                {chatMaximize ? (
                  <Box
                    sx={{ color: "white", cursor: "pointer" }}
                    onClick={() => setChatMaximize(!chatMaximize)}
                  >
                    <Icon icon="tabler:arrows-diagonal-minimize-2" />
                  </Box>
                ) : (
                  <Box
                    sx={{ color: "white", cursor: "pointer" }}
                    onClick={() => setChatMaximize(!chatMaximize)}
                  >
                    <Icon icon="tabler:arrows-diagonal" />
                  </Box>
                )}
              </Box>
            }
            sx={{
              // backgroundColor: theme.palette.main.main,
              backgroundImage: 'linear-gradient(to right, rgb(0, 58, 112, 0.9), #23A26D)'
            }}
          />
          <CardContent
            className="app-chat"
            sx={{
              flexGrow: 1,
              display: "flex",
              height: "100%",
              alignItems: chatIsEmpty ? "center" : undefined,
              padding: "16px 10px !important",
            }}
          >
            {chatIsEmpty ? (
              <ChatWrapperStartChat>
                <Box component="img" src="images/Logo2.svg" alt="Cleri Logo" />
                <Typography variant="h6">How can I help?</Typography>
                <Typography variant="body2" color="secondary">
                  Give me tasks, and I'll complete them is seconds.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      px: 6,
                      py: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                    }}
                  >
                    <Icon icon="tabler:components" />
                    <Typography variant="body2">Send Bulk Invoices</Typography>
                  </Box>
                  <Box
                    sx={{
                      px: 6,
                      py: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                    }}
                  >
                    <Icon icon="tabler:components" />
                    <Typography variant="body2">Create New Estimate</Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      px: 6,
                      py: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                    }}
                  >
                    <Icon icon="tabler:components" />
                    <Typography variant="body2">
                      Review Budgeted vs Actual Costs
                    </Typography>
                  </Box>
                </Box>
              </ChatWrapperStartChat>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <ChatLog theme={theme} />
                <SendMsgForm />
              </Box>
            )}
          </CardContent>
        </Card>
        <Box
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            cursor: "pointer",
          }}
        >
          <Box
            component="img"
            src={ChatBotIcon}
            alt="Chatbot Icon"
            onClick={() => setDisplayChat(!displayChat)}
            sx={{
              position: "absolute",
              right: 0,
              bottom: 0,
              width: 60,
              transition: "opacity 0.3s ease-in-out",
              opacity: isHovered ? '1' : '0.5',
            }}
          />
          {/* <Box
            component="img"
            src={ChatBotIconExpanded}
            alt="Chatbot Icon Hover"
            onClick={() => setDisplayChat(!displayChat)}
            sx={{
              position: "absolute",
              right: 0,
              bottom: 0,
              transition: "opacity 0.3s ease-in-out",
              opacity: isHovered ? 1 : 0,
            }}
          /> */}
        </Box>
      </Box>
    </>
  );
};

export default Chatbot;
